import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Calculator, Loader2, Key } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { posRegisterService } from '../../services/pos.service'
import { CatalogSelect } from '../ui/CatalogSelect'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'

interface RegisterAssignmentModalProps {
  open: boolean
  onSuccess: (registerData: any) => void
  expectedRegisterId?: number | null
}

export function RegisterAssignmentModal({ open, onSuccess, expectedRegisterId }: RegisterAssignmentModalProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [selectedRegisterId, setSelectedRegisterId] = useState<number | null>(null)
  const [pin, setPin] = useState('')

  useEffect(() => {
    if (open) {
      queryClient.invalidateQueries({ queryKey: ['available-registers'] })
      queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'catalogSearch' })
    }
  }, [open, queryClient])

  // The modal uses CatalogSelect which fetches data internally,
  // but we might still want to check if there are any registers
  // to disable the input or show a message.
  const { data: availableRegisters = [], isLoading: isLoadingRegisters } = useQuery({
    queryKey: ['available-registers'],
    queryFn: posRegisterService.getAvailableRegisters,
    enabled: open,
  })

  const assignMutation = useMutation({
    mutationFn: (payload: { pin: string; cash_register_id: number; expected_cash_register_id: number | null }) =>
      posRegisterService.updateRegisterAssignment(payload),
    onSuccess: (data) => {
      toast.success('Caja asignada exitosamente')
      setPin('')
      setSelectedRegisterId(null)
      onSuccess(data)
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message ?? 'No se pudo asignar la caja. Verifica el PIN o intenta nuevamente.'
      toast.error(msg)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedRegisterId) {
      toast.error('Selecciona una caja')
      return
    }
    if (pin.length < 4) {
      toast.error('El PIN debe tener al menos 4 dígitos')
      return
    }
    assignMutation.mutate({ pin, cash_register_id: selectedRegisterId, expected_cash_register_id: expectedRegisterId ?? null })
  }

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      {/* Remove the default cross and prevent close on outside click by overriding onOpenChange */}
      <DialogContent className="sm:max-w-md [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl text-primary">
            <Calculator className="h-6 w-6" />
            Asignación de Caja
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600 pt-2 pb-4">
            ¡Bienvenido al Punto de Venta! Para comenzar a operar en este equipo, necesitas asignar una caja. Esto te permitirá procesar ventas y administrar el flujo de efectivo en tu turno. Por favor, selecciona una caja disponible e ingresa tu PIN de acceso.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">Cajas Disponibles</label>
              {isLoadingRegisters ? (
                <div className="flex items-center justify-center p-4 border rounded-md bg-slate-50">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              ) : availableRegisters.length === 0 ? (
                <div className="p-4 border rounded-md bg-red-50 text-red-600 text-sm text-center">
                  No hay cajas disponibles o registradas. Contacta al administrador.
                </div>
              ) : (
                <CatalogSelect
                  catalog="pos/available-registers"
                  placeholder="-- Selecciona una caja --"
                  value={selectedRegisterId ?? ''}
                  onChange={(val) => setSelectedRegisterId(val ? Number(val) : null)}
                  itemLabelFormatter={(item: any) => `${item.name} (${item.code}) ${item.sucursal ? `- ${item.sucursal.name}` : ''}`}
                />
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">PIN de Autorización</label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="password"
                  required
                  maxLength={8}
                  pattern="\d+"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="Ej. 1234"
                  className="pl-10 h-11 font-mono tracking-widest text-lg"
                  disabled={availableRegisters.length === 0}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:space-x-2">
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto h-11"
              onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/')}
            >
              Regresar
            </Button>
            <Button
              type="submit"
              className="w-full sm:w-auto h-11 text-base bg-primary hover:bg-primary/90 flex-1"
              disabled={assignMutation.isPending || !selectedRegisterId || !pin || availableRegisters.length === 0}
            >
              {assignMutation.isPending ? (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              ) : (
                <Calculator className="mr-2 h-5 w-5" />
              )}
              Asignar y Comenzar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
